import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { ROLE_LABEL } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, UnitPicker } from "@/components/Shared";

const EMPTY = { name: "", username: "", email: "", password: "", role: "viewer", units: [], active: true };
const ROLE_DESC = {
  admin: "Kelola pengguna, konfigurasi sync, lihat semua data",
  manajemen: "Lihat semua unit + ringkasan agregat",
  operasional: "Hanya data unit yang ditetapkan",
  viewer: "Baca saja (semua unit, atau unit tertentu jika dipilih)",
};

function UserDialog({ editing, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (editing) setForm(editing === "new" ? EMPTY : { ...editing, password: "" }); }, [editing]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, password: form.password || null };
      if (editing === "new") await api.post("/users", body);
      else await api.put(`/users/${editing.id}`, body);
      toast.success("Pengguna disimpan");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const needUnits = form.role === "operasional" || form.role === "viewer";
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editing === "new" ? "Tambah Pengguna" : "Ubah Pengguna"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5"><Label>Nama</Label><Input data-testid="user-form-name" value={form.name} onChange={(e) => set("name")(e.target.value)} /></div>
          <div className="grid gap-1.5"><Label>Username (untuk login)</Label><Input data-testid="user-form-username" value={form.username || ""} onChange={(e) => set("username")(e.target.value.toLowerCase())} /></div>
          <div className="grid gap-1.5"><Label>Email <span className="text-muted-foreground">(opsional)</span></Label><Input data-testid="user-form-email" type="email" value={form.email || ""} onChange={(e) => set("email")(e.target.value)} /></div>
          <div className="grid gap-1.5"><Label>Kata sandi {editing !== "new" && <span className="text-muted-foreground">(kosongkan jika tidak diubah)</span>}</Label>
            <Input data-testid="user-form-password" type="password" value={form.password} onChange={(e) => set("password")(e.target.value)} /></div>
          <div className="grid gap-1.5"><Label>Peran</Label>
            <Select value={form.role} onValueChange={set("role")}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(ROLE_LABEL).map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{ROLE_DESC[form.role]}</p>
          </div>
          {needUnits && <div className="grid gap-1.5"><Label>Unit {form.role === "operasional" ? "(wajib)" : "(opsional)"}</Label><UnitPicker testid="user-form-units" value={form.units} onChange={set("units")} /></div>}
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="user-form-active" checked={form.active} onCheckedChange={set("active")} />Aktif</label>
        </div>
        <DialogFooter><Button data-testid="user-form-save" onClick={save} disabled={busy}>Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/users").then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (u) => {
    if (!window.confirm(`Hapus pengguna ${u.username}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Pengguna dihapus"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader eyebrow="Administrasi" title="Pengguna & Peran" desc="Tidak ada pendaftaran terbuka — hanya admin yang dapat membuat akun.">
        <Button data-testid="add-user-button" onClick={() => setEditing("new")}><Plus className="mr-1.5 h-4 w-4" />Tambah Pengguna</Button>
      </PageHeader>
      <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
        <table data-testid="users-table" className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
            <tr><th className="p-3 text-left">Nama</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Peran</th><th className="p-3 text-left">Unit</th><th className="p-3 text-left">Status</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.username}`} className="border-t">
                <td className="p-3 font-medium">{u.name}</td>
                <td className="p-3"><span className="font-num">{u.username}</span>{u.email && <span className="block text-xs text-muted-foreground">{u.email}</span>}</td>
                <td className="p-3"><span className="rounded-full bg-[#0A3967]/10 px-2 py-0.5 text-xs font-semibold text-[#0A3967] dark:bg-white/10 dark:text-white">{ROLE_LABEL[u.role]}</span></td>
                <td className="p-3 text-xs">{u.units?.length ? u.units.join(", ") : "Semua"}</td>
                <td className="p-3 text-xs">{u.active ? "Aktif" : "Nonaktif"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button data-testid={`edit-user-${u.username}`} size="icon" variant="ghost" onClick={() => setEditing(u)}><Pencil className="h-4 w-4" /></Button>
                  {u.id !== me.id && <Button data-testid={`delete-user-${u.username}`} size="icon" variant="ghost" onClick={() => remove(u)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <UserDialog editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
